import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';

/**
 * ComplianceManager
 *
 * Reusable UI for the Staff "My Compliance" tab and the Admin "Compliance"
 * panel. Decides which mode to render based on `mode`:
 *
 *   • mode="self"  — staff member sees ONLY their own records, can upload
 *                    new evidence, edit dates, and replace expiring certs.
 *   • mode="staff" — admin reviews ONE staff member's records, can verify
 *                    or reject each one. Pass `staffId` + `staffName`.
 *
 * Data model exposed to the user
 * ------------------------------
 * Each "row" in the list is a *catalogue entry* (kind + label + required +
 * record?). When `record` is null the row is a "missing" placeholder
 * inviting the staff to upload. When present it shows the latest doc + a
 * status badge (verified / expiring / expired / pending / rejected).
 *
 * The component owns its own data — call sites just provide identifying
 * props. All mutations go through `api.*ComplianceRecord` endpoints.
 */

const STATUS_CONFIG = {
  verified:       { label: 'Verified',           bg: 'hsl(150 60% 92%)', fg: 'hsl(150 60% 25%)', border: 'hsl(150 50% 75%)' },
  expiring_soon:  { label: 'Expiring soon',      bg: 'hsl(35 90% 92%)',  fg: 'hsl(30 80% 28%)',  border: 'hsl(35 80% 75%)' },
  expired:        { label: 'Expired',            bg: 'hsl(0 80% 95%)',   fg: 'hsl(0 70% 35%)',   border: 'hsl(0 70% 80%)' },
  pending_verification: { label: 'Pending review', bg: 'hsl(220 80% 95%)', fg: 'hsl(220 70% 35%)', border: 'hsl(220 70% 80%)' },
  rejected:       { label: 'Rejected',           bg: 'hsl(0 80% 95%)',   fg: 'hsl(0 70% 35%)',   border: 'hsl(0 70% 80%)' },
  missing:        { label: 'Missing',            bg: 'hsl(var(--muted))', fg: 'hsl(var(--muted-foreground))', border: 'hsl(var(--border))' },
};

function StatusBadge({ status, testId }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.missing;
  return (
    <span
      data-testid={testId}
      style={{
        padding: '0.25rem 0.65rem',
        borderRadius: '999px',
        background: cfg.bg,
        color: cfg.fg,
        border: `1px solid ${cfg.border}`,
        fontSize: '0.75rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </span>
  );
}

function dateInputValue(v) {
  if (!v) return '';
  return String(v).slice(0, 10);
}

function fmtDate(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString();
  } catch (e) {
    return String(v);
  }
}

export function ComplianceManager({ mode = 'self', staffId = null, staffName = null, onChanged }) {
  const isAdminMode = mode === 'staff';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [labels, setLabels] = useState({});
  const [catalogue, setCatalogue] = useState([]);
  // Editing state for the per-row inline form. Keyed by catalogue kind so
  // multiple rows can be open at once without colliding.
  const [editing, setEditing] = useState({}); // kind -> { reference_number, issued_on, expires_on, notes, file_id, file_url }
  const [uploadingKind, setUploadingKind] = useState(null);
  const [savingKind, setSavingKind] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [lbls, payload] = await Promise.all([
        api.getComplianceLabels(),
        isAdminMode
          ? api.listComplianceRecords(staffId).then((records) => ({
              catalogue: buildCatalogueFromRecords(records, lblsCacheRef),
              records,
            }))
          : api.getMyCompliance(),
      ]);
      setLabels(lbls || {});
      setCatalogue(payload.catalogue || []);
    } catch (err) {
      setError(err.message || 'Could not load compliance data');
    } finally {
      setLoading(false);
    }
  };

  // Keep a stable ref to the latest labels so we can synthesise catalogue
  // entries from records when the admin loads someone else's list.
  const lblsCacheRef = labels;

  function buildCatalogueFromRecords(records, lbls) {
    return (records || []).map((r) => ({
      kind: r.kind,
      label: r.label || (lbls && lbls[r.kind]) || r.kind,
      required: false,
      status: r.status,
      record: r,
    }));
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId, mode]);

  const beginEdit = (entry) => {
    const r = entry.record || {};
    setEditing({
      ...editing,
      [entry.kind]: {
        kind: entry.kind,
        reference_number: r.reference_number || '',
        issued_on: dateInputValue(r.issued_on),
        expires_on: dateInputValue(r.expires_on),
        notes: r.notes || '',
        file_id: r.file_id || '',
        file_url: r.file_url || '',
      },
    });
  };

  const cancelEdit = (kind) => {
    const next = { ...editing };
    delete next[kind];
    setEditing(next);
  };

  const handleFile = async (entry, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large — max 10 MB');
      return;
    }
    try {
      setUploadingKind(entry.kind);
      const upload = await api.uploadFile(file, { category: 'documents', linkedType: 'compliance' });
      setEditing((prev) => ({
        ...prev,
        [entry.kind]: {
          ...(prev[entry.kind] || { kind: entry.kind }),
          file_id: upload.file_id,
          file_url: upload.url,
          _file_name: file.name,
        },
      }));
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploadingKind(null);
    }
  };

  const handleSave = async (entry) => {
    const draft = editing[entry.kind];
    if (!draft) return;
    // Partial saves are explicitly allowed — staff may save what they
    // have today and fill in expiry / file later. We only ask for kind
    // (which is always set by the catalogue row).
    const payload = {
      kind: entry.kind,
      reference_number: draft.reference_number || null,
      issued_on: draft.issued_on ? new Date(draft.issued_on).toISOString() : null,
      expires_on: draft.expires_on ? new Date(draft.expires_on).toISOString() : null,
      notes: draft.notes || null,
      file_id: draft.file_id || null,
    };
    if (isAdminMode) payload.staff_id = staffId;
    try {
      setSavingKind(entry.kind);
      setError('');
      if (entry.record && entry.record._id) {
        await api.updateComplianceRecord(entry.record._id, payload);
      } else {
        await api.createComplianceRecord(payload);
      }
      cancelEdit(entry.kind);
      await loadData();
      // Surface a transient "Saved" banner so staff see their progress
      // landed even when no file/dates were attached (the row will
      // continue to show "missing" in that case, which previously made
      // them think the save had failed).
      setSuccess(`Saved progress for ${entry.label}. You can come back and add more later.`);
      setTimeout(() => setSuccess(''), 4000);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSavingKind(null);
    }
  };

  const handleVerify = async (entry) => {
    if (!entry.record) return;
    try {
      await api.verifyComplianceRecord(entry.record._id);
      await loadData();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message || 'Verify failed');
    }
  };

  const handleReject = async (entry) => {
    if (!entry.record) return;
    const reason = window.prompt('Reason for rejection (visible to the staff member):');
    if (!reason) return;
    try {
      await api.rejectComplianceRecord(entry.record._id, reason);
      await loadData();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message || 'Reject failed');
    }
  };

  const handleDelete = async (entry) => {
    if (!entry.record) return;
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    try {
      await api.deleteComplianceRecord(entry.record._id);
      await loadData();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const headline = useMemo(() => {
    const counts = catalogue.reduce((acc, c) => {
      acc[c.status] = (acc[c.status] || 0) + 1;
      return acc;
    }, {});
    return counts;
  }, [catalogue]);

  if (loading) return <div data-testid="compliance-loading">Loading compliance records…</div>;

  return (
    <div data-testid="compliance-manager">
      {/* Persistent help banner (self mode only) — addresses the user
          report that staff thought partial saves were blocked. */}
      {!isAdminMode && (
        <div
          className="card"
          style={{ marginBottom: '1rem', background: 'hsl(220 60% 97%)', borderColor: 'hsl(220 70% 80%)' }}
          data-testid="compliance-help-banner"
        >
          ℹ️ <strong>Save what you have today.</strong> You can save each compliance row with as much information as you currently have (e.g. just a reference number) and come back later to upload the certificate or add the expiry date. Required items will keep their "Missing" status until everything is provided, but your progress is always stored.
        </div>
      )}

      {success && (
        <div
          className="card"
          data-testid="compliance-success"
          style={{ marginBottom: '1rem', borderColor: 'hsl(150 60% 60%)', background: 'hsl(150 60% 96%)', color: 'hsl(150 80% 20%)' }}
        >
          ✅ {success}
        </div>
      )}

      {error && (
        <div
          className="card"
          data-testid="compliance-error"
          style={{ marginBottom: '1rem', borderColor: 'hsl(0 70% 70%)', background: 'hsl(0 70% 97%)' }}
        >
          ⚠️ {error}
          <button type="button" className="btn btn-ghost" style={{ marginLeft: '1rem' }} onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1rem' }} data-testid="compliance-headline">
        {['verified', 'expiring_soon', 'expired', 'pending_verification', 'rejected', 'missing'].map((s) =>
          headline[s] ? (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <StatusBadge status={s} />
              <strong>{headline[s]}</strong>
            </div>
          ) : null,
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {catalogue.length === 0 && (
          <div className="card">
            <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))' }}>
              No compliance records yet.
              {!isAdminMode && ' Tap "Upload" on any of the required certificates to get started.'}
            </p>
          </div>
        )}
        {catalogue.map((entry) => {
          const draft = editing[entry.kind];
          const r = entry.record || {};
          const isOpen = !!draft;
          return (
            <div key={`${entry.kind}-${r._id || 'new'}`} className="card" data-testid={`compliance-row-${entry.kind}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div>
                  <strong>{entry.label}</strong>{' '}
                  {entry.required && <span style={{ fontSize: '0.7rem', color: 'hsl(0 70% 45%)', fontWeight: 600 }}>· REQUIRED</span>}
                  <div style={{ fontSize: '0.825rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.2rem' }}>
                    {r.reference_number && <>Ref: <strong>{r.reference_number}</strong> · </>}
                    {r.issued_on && <>Issued: {fmtDate(r.issued_on)} · </>}
                    {r.expires_on && <>Expires: <strong>{fmtDate(r.expires_on)}</strong></>}
                    {r.rejected_reason && <span style={{ color: 'hsl(0 70% 45%)' }}> · Rejected: {r.rejected_reason}</span>}
                    {r.verified_at && <>· Verified {fmtDate(r.verified_at)}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <StatusBadge status={entry.status} testId={`compliance-status-${entry.kind}`} />
                  {r.file_url && (
                    <a
                      href={r.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-ghost"
                      style={{ padding: '0.2rem 0.55rem', fontSize: '0.78rem' }}
                      data-testid={`compliance-view-${entry.kind}`}
                    >
                      📎 View
                    </a>
                  )}
                  {!isOpen && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ padding: '0.2rem 0.6rem', fontSize: '0.78rem' }}
                      onClick={() => beginEdit(entry)}
                      data-testid={`compliance-edit-${entry.kind}`}
                    >
                      {entry.record ? 'Edit / Replace' : 'Upload'}
                    </button>
                  )}
                  {isAdminMode && r._id && entry.status !== 'verified' && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ padding: '0.2rem 0.6rem', fontSize: '0.78rem' }}
                      onClick={() => handleVerify(entry)}
                      data-testid={`compliance-verify-${entry.kind}`}
                    >
                      ✅ Verify
                    </button>
                  )}
                  {isAdminMode && r._id && entry.status !== 'rejected' && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ padding: '0.2rem 0.6rem', fontSize: '0.78rem' }}
                      onClick={() => handleReject(entry)}
                      data-testid={`compliance-reject-${entry.kind}`}
                    >
                      ❌ Reject
                    </button>
                  )}
                  {r._id && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ padding: '0.2rem 0.6rem', fontSize: '0.78rem', color: 'hsl(0 70% 45%)' }}
                      onClick={() => handleDelete(entry)}
                      data-testid={`compliance-delete-${entry.kind}`}
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>

              {isOpen && (
                <div style={{ marginTop: '0.85rem', display: 'grid', gap: '0.6rem', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                  <div className="form-group">
                    <label>Reference / certificate number</label>
                    <input
                      type="text"
                      value={draft.reference_number}
                      onChange={(e) => setEditing({ ...editing, [entry.kind]: { ...draft, reference_number: e.target.value } })}
                      data-testid={`compliance-ref-${entry.kind}`}
                    />
                  </div>
                  <div className="form-group">
                    <label>Issued on</label>
                    <input
                      type="date"
                      value={draft.issued_on}
                      onChange={(e) => setEditing({ ...editing, [entry.kind]: { ...draft, issued_on: e.target.value } })}
                      data-testid={`compliance-issued-${entry.kind}`}
                    />
                  </div>
                  <div className="form-group">
                    <label>Expires on</label>
                    <input
                      type="date"
                      value={draft.expires_on}
                      onChange={(e) => setEditing({ ...editing, [entry.kind]: { ...draft, expires_on: e.target.value } })}
                      data-testid={`compliance-expires-${entry.kind}`}
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Upload evidence (PDF / image, max 10 MB)</label>
                    <input
                      type="file"
                      accept="application/pdf,image/png,image/jpeg,image/webp"
                      onChange={(e) => handleFile(entry, e.target.files && e.target.files[0])}
                      data-testid={`compliance-file-${entry.kind}`}
                    />
                    {uploadingKind === entry.kind && <small>Uploading…</small>}
                    {(draft.file_url || draft._file_name) && (
                      <small style={{ display: 'block', marginTop: '0.2rem' }}>
                        {draft._file_name ? `Selected: ${draft._file_name}` : 'Existing file kept'}
                      </small>
                    )}
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Notes</label>
                    <textarea
                      rows={2}
                      value={draft.notes}
                      onChange={(e) => setEditing({ ...editing, [entry.kind]: { ...draft, notes: e.target.value } })}
                      data-testid={`compliance-notes-${entry.kind}`}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => cancelEdit(entry.kind)}
                      data-testid={`compliance-cancel-${entry.kind}`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={savingKind === entry.kind}
                      onClick={() => handleSave(entry)}
                      data-testid={`compliance-save-${entry.kind}`}
                    >
                      {savingKind === entry.kind ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {staffName && isAdminMode && (
        <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
          Reviewing records for <strong>{staffName}</strong>.
        </p>
      )}
    </div>
  );
}

export default ComplianceManager;
